import { FC, ReactNode } from "react";
import PropTypes from "prop-types";
import useMediaQuery from "@mui/material/useMediaQuery";
import type { Theme } from "@mui/material/styles/createTheme";
import { styled } from "@mui/material/styles";
import { useDialog } from "src/hooks/use-dialog";

import type { NavColor } from "src/types/settings";

import type { Section } from "../config";
import { MobileNav } from "../mobile-nav";
import { SideNav } from "./side-nav";
import { TopNav } from "./top-nav";
import { useMobileNav } from "./use-mobile-nav";
import { TopReadonly } from "./top-readonly";
import { TopAdmin } from "./top-admin";
import { TopDemo } from "./top-demo";
import { useSections } from "../config";

import useUserDataProvider from "src/contexts/userData-context";

import useHousehold, { ExperienceFlag } from "src/contexts/household-context";

const SIDE_NAV_WIDTH: number = 280;

const VerticalLayoutRoot = styled("div")(({ theme }) => ({
  display: "flex",
  flex: "1 1 auto",
  maxWidth: "100%",
  [theme.breakpoints.up("md")]: {
    paddingLeft: SIDE_NAV_WIDTH,
  },
}));

const VerticalLayoutContainer = styled("div")({
  display: "flex",
  flex: "1 1 auto",
  flexDirection: "column",
  width: "100%",
});

interface VerticalLayoutProps {
  children?: ReactNode;
  navColor?: NavColor;
  sections?: Section[];
}

export const VerticalLayout: FC<VerticalLayoutProps> = (props) => {
  const { children, navColor } = props;
  const sections = useSections();
  const lgUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));
  const mobileNav = useMobileNav();
  const addPlanDialog = useDialog();
  const {
    userData
  } = useUserDataProvider();

  const { selectedHousehold, experienceFlag } = useHousehold();

  return (
    <>
      {experienceFlag === ExperienceFlag.READONLY && (
        <TopReadonly
          onMobileNavOpen={mobileNav.handleOpen}
          userData={userData}
        />
      )}
      {experienceFlag === ExperienceFlag.PROFESSIONAL && (
        <TopAdmin
          onMobileNavOpen={mobileNav.handleOpen}
        />
      )}
      {selectedHousehold?.household_role?.is_demo_client && (
        <TopDemo onMobileNavOpen={mobileNav.handleOpen} />
      )}
      <TopNav
        onMobileNavOpen={mobileNav.handleOpen}
        admin={experienceFlag === ExperienceFlag.PROFESSIONAL || experienceFlag === ExperienceFlag.READONLY}
      />
      {lgUp && (
        <SideNav
          color={navColor}
          sections={sections}
          onAddPlanClick={addPlanDialog.handleOpen}
          admin={experienceFlag === ExperienceFlag.PROFESSIONAL || experienceFlag === ExperienceFlag.READONLY}
        />
      )}
      {!lgUp && (
        <MobileNav
          color={navColor}
          onClose={mobileNav.handleClose}
          open={mobileNav.open}
          sections={sections}
          onAddPlanClick={addPlanDialog.handleOpen}
        />
      )}
      <VerticalLayoutRoot
        sx={{
          paddingTop:
            (experienceFlag === ExperienceFlag.READONLY || experienceFlag === ExperienceFlag.PROFESSIONAL) &&
              lgUp
              ? "48px"
              : "0px",
        }}
      >
        <VerticalLayoutContainer>
          {children}
          {/* <VerifyUserDialog
            onCancel={cancelVerifyDialog}
            open={verifyUserDialog.open}
            onEditSuccess={onEditSuccess}
            userDataToInvite={
              targetAuthRoles.find((r) => r.user_data?.ud_id === selectedId)
                ?.user_data || {
                ud_id: null,
                contact_email: "",
              }
            }
            peopleRolesSavingNew={peopleRolesSavingNew}
            // filter out people with relationship of pet or charity and also all people who already exist on the list of executors
            handleInviteFromVerifyDialog={handleInviteFromVerifyDialog}
          /> */}
        </VerticalLayoutContainer>
      </VerticalLayoutRoot>
    </>
  );
};

VerticalLayout.propTypes = {
  children: PropTypes.node as PropTypes.Validator<ReactNode>,
  navColor: PropTypes.oneOf<NavColor>(["blend-in", "discreet", "evident"]),
  sections: PropTypes.array,
};
